import React, { useEffect, useState } from 'react';

const ZKAS_ADDRESS = 'zkas:px34dgawfhyrr6xtelm52fae476rcrfqs5q0ex9w62wft9hytlf3rn79nm8cd9acxnthl2cp7tkqqj8';
let zkasFindsRequest;

const fetchZkasFinds = () => {
  if (!zkasFindsRequest) {
    const isLocal = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
      || window.location.hostname.endsWith('.local');
    const url = isLocal ? '/api-local/rustykaspa-zkas' : '/.netlify/functions/rustykaspa-zkas';
    zkasFindsRequest = fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ zkasAddress: ZKAS_ADDRESS }),
    }).then(async response => {
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || 'ZKAS lookup failed');
      return data;
    }).catch(error => {
      zkasFindsRequest = null;
      throw error;
    });
  }
  return zkasFindsRequest;
};

const formatHashrate = (hashrate) => {
  const units = ['H/s', 'KH/s', 'MH/s', 'GH/s', 'TH/s', 'PH/s', 'EH/s'];
  let value = (Number(hashrate) || 0) * 1e9;
  let unitIndex = 0;
  while (value >= 1000 && unitIndex < units.length - 1) {
    value /= 1000;
    unitIndex += 1;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${units[unitIndex]}`;
};

const getHashrateColor = (worker) => (
  worker.status === 'online' && Number(worker.hashrate) > 0 ? '#22c55e' : '#ef4444'
);

const formatLastSeen = (value, fetchTime) => {
  if (typeof value !== 'number' || !fetchTime) return 'N/A';
  const timestamp = value > 1e12 ? value : value * 1000;
  const seconds = Math.max(0, Math.floor((fetchTime - timestamp) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  return `${Math.floor(seconds / 60)}m ago`;
};

const RustyKaspaTable = ({ kaspaData, error, kaspaFetchTime, showSummary = true, showWorkers = true, isDetailsTab = false, zkasFinds = null, onZkasFindsChange }) => {
  const [zkasResult, setZkasResult] = useState(null);
  const [zkasError, setZkasError] = useState(null);
  const [zkasLoading, setZkasLoading] = useState(false);
  const [showZkasFinds, setShowZkasFinds] = useState(false);
  const liveWorkers = kaspaData?.aggregateStats?.workers;
  const topLevelWorkers = kaspaData?.workers;
  const bridgeWorkers = (kaspaData?.bridges || []).flatMap(bridge => bridge.workers || []);
  const workers = Array.isArray(liveWorkers) && liveWorkers.length > 0
    ? liveWorkers
    : (Array.isArray(topLevelWorkers) && topLevelWorkers.length > 0 ? topLevelWorkers : bridgeWorkers);
  const totalHashrate = workers.reduce((sum, worker) => sum + (Number(worker.hashrate) || 0), 0);
  const totalBlocks = workers.reduce((sum, worker) => sum + (Number(worker.blocks) || 0), 0);
  const onlineWorkers = workers.filter(worker => worker.status === 'online').length;
  const overallHashrateColor = totalHashrate <= 0 || onlineWorkers === 0
    ? '#ef4444'
    : onlineWorkers === workers.length
      ? '#22c55e'
      : '#f59e42';
  const workerCountColor = workers.length > 0 && onlineWorkers === workers.length ? '#22c55e' : '#ef4444';
  const zkasFindCount = zkasResult?.finds?.count ?? zkasResult?.finds?.finds?.length ?? zkasFinds;

  useEffect(() => {
    let cancelled = false;

    const verifyZkas = async () => {
    setZkasLoading(true);
    setZkasError(null);
    try {
      const data = await fetchZkasFinds();
      if (!cancelled) {
        setZkasResult(data);
        onZkasFindsChange?.(data.finds?.count ?? data.finds?.finds?.length ?? 0);
      }
    } catch (verificationError) {
      if (!cancelled) {
        setZkasResult(null);
        onZkasFindsChange?.(null);
        setZkasError(verificationError.message);
      }
    } finally {
      setZkasLoading(false);
    }
    };
    verifyZkas();
    return () => { cancelled = true; };
  }, [isDetailsTab, showWorkers, onZkasFindsChange]);

  return (
    <div className="column is-12" style={{ marginBottom: 16 }}>
      <div className="box" style={{ background: '#18181b', color: '#fff', border: '1px solid #70C7BA', borderRadius: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 18 }}>RustyKaspa Pool</span>
        </div>
        {error && <div style={{ color: '#ef4444', marginBottom: 8, fontSize: 12, padding: 8, background: '#1f0000', borderRadius: 4 }}>{error}</div>}
        {!kaspaData && !error && <div>Loading...</div>}
        {kaspaData && (
          <>
            {showSummary && (
              <div style={{ display: 'flex', gap: 12, marginBottom: showWorkers ? 20 : 0, flexWrap: 'wrap' }}>
                {[
                  ['Hashrate', formatHashrate(totalHashrate), overallHashrateColor],
                  ['Workers', `${onlineWorkers} / ${workers.length}`, workerCountColor],
                  ['KAS blocks', totalBlocks.toLocaleString(), undefined],
                  ['ZKAS blocks', zkasFindCount == null ? '—' : Number(zkasFindCount).toLocaleString(), undefined],
                ].map(([label, value, color]) => (
                  <div key={label} style={{ flex: '1 1 140px', background: '#23272f', borderRadius: 6, padding: 12, border: '1px solid #333' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
                    <div style={{ fontSize: 12, color: '#a3a3a3' }}>{label}</div>
                  </div>
                ))}
              </div>
            )}
            {showWorkers && <>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Workers</h2>
            <div style={{ overflowX: 'auto' }}>
              <table className="table is-fullwidth is-hoverable" style={{ minWidth: 680, background: '#23272f', borderRadius: 6 }}>
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th style={{ textAlign: 'right' }}>Hashrate</th>
                    <th style={{ textAlign: 'right' }}>Shares</th>
                    <th style={{ textAlign: 'right' }}>Blocks</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {workers.length > 0 ? workers.map((worker, index) => (
                    <tr key={`${worker.worker}-${worker.instance || index}`} style={{ opacity: worker.status === 'offline' ? 0.5 : 1 }}>
                      <td>{worker.worker === 'kspro3' ? 'ks5pro3' : worker.worker === 'k5pro2' ? 'ks5pro2' : worker.worker}</td>
                      <td style={{ textAlign: 'right', color: getHashrateColor(worker) }}>{formatHashrate(worker.hashrate)}</td>
                      <td style={{ textAlign: 'right' }}>{Number(worker.shares || 0).toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>{Number(worker.blocks || 0).toLocaleString()}</td>
                      <td>{worker.status || 'Unknown'}</td>
                      <td style={{ textAlign: 'right' }}>{formatLastSeen(worker.lastSeen, kaspaFetchTime)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#a3a3a3' }}>No workers found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            </>}
            {isDetailsTab && showWorkers && (
              <div style={{ marginTop: 16, borderTop: '1px solid #333', paddingTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: showZkasFinds ? 8 : 0 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>ZKAS Finds</h2>
                  <button
                    type="button"
                    aria-expanded={showZkasFinds}
                    onClick={() => setShowZkasFinds(visible => !visible)}
                    style={{ background: '#23272f', color: '#70C7BA', border: '1px solid #70C7BA', borderRadius: 4, padding: '6px 10px', cursor: 'pointer' }}
                  >
                    {showZkasFinds ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showZkasFinds && <>
                {zkasLoading && <div style={{ color: '#a3a3a3', fontSize: 12 }}>Loading verified ZKAS finds...</div>}
                {zkasError && <div style={{ color: '#ef4444', marginTop: 8, fontSize: 12 }}>{zkasError}</div>}
                {zkasResult && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ color: '#22c55e', marginBottom: 8 }}>
                      Verified. ZKAS blocks: {zkasFindCount || 0}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="table is-fullwidth is-hoverable" style={{ background: '#23272f' }}>
                        <thead><tr><th>ZKAS block</th><th>Accepted</th><th>Role</th></tr></thead>
                        <tbody>{(zkasResult.finds?.finds || []).map(find => (
                          <tr key={find.zkas_block_hash}>
                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{find.zkas_block_hash}</td>
                            <td>{find.accepted_at_ms ? new Date(find.accepted_at_ms).toLocaleString('en-GB') : '-'}</td>
                            <td>{find.role || '-'}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                )}
                </>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RustyKaspaTable;
